import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { en } from '@/i18n/locales/en'
import { ru } from '@/i18n/locales/ru'

/* Bilingual UI, Russian and English, per-user switch (FR-58, NFR-8).
 *
 * Russian is NOT a translation layer over an English product (EXPERIENCE.md
 * § Voice and Tone). Both catalogues are authored to read naturally, and the
 * Russian one uses the TRADE'S OWN vocabulary rather than translated SaaS
 * vocabulary — `Работа` for a case, the word already on their Master File sheet
 * tab, `Ставка` for the client rate, `Расчётные`/`Фактические затраты` for
 * planned and actual costs. The PRD glossary is the authority; where it and
 * generic terminology disagree, the PRD wins.
 *
 * The catalogues are BUNDLED, not fetched. There are two of them, they are a
 * few kilobytes each, and a lazy-loaded namespace would mean a flash of keys on
 * a language switch — this product's users switch language once and stay.
 */

export const SUPPORTED_LANGUAGES = ['en', 'ru'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: Language = 'en'

/** Where the choice is persisted for anonymous/pre-session paints. The server
 *  is the real home of the preference (`CurrentUser.language`) — this only
 *  avoids a flash of the wrong language before the session query resolves. */
export const LANGUAGE_STORAGE_KEY = 'asa.language'

export function isLanguage(value: unknown): value is Language {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  )
}

/** The BCP 47 tag used for `Intl` and the `<html lang>` attribute. `en-GB`
 *  rather than `en-US`: dates read `4 Aug 2026`, not `Aug 4, 2026` (DESIGN.md). */
export const LOCALE_TAGS: Record<Language, string> = {
  en: 'en-GB',
  ru: 'ru-RU',
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ru: { translation: ru },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // `en-GB` and `ru-RU` both resolve to their base catalogue rather than
    // missing it, so a browser reporting a regional tag is not sent to English.
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React escapes for us; i18next doing it again turns `Türkmenbaşy` and
      // the `→` in a route into entities.
      escapeValue: false,
    },
    // A missing key must be loud in development and invisible in production:
    // showing a raw dotted key to a user is worse than showing the English.
    returnEmptyString: false,
    debug: import.meta.env.DEV,
  })

export default i18n

/** Any valid key in the catalogues. Components that accept a key as a prop —
 *  TabNav, navigation items — use this so a typo is a build error rather than a
 *  dotted string rendered to a user. */
export type TranslationKey = Parameters<typeof i18n.t>[0]
